import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { isAuthenticated, token, user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      setSubscription({
        status: 'free',
        plan: 'free',
        can_access_all_stories: false,
        can_use_tts: false,
        free_stories_limit: 2,
        trial_available: true
      });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const startTrial = async () => {
    try {
      const response = await axios.post(`${API}/subscription/start-trial`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchSubscription();
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Er ging iets mis' };
    }
  };

  const subscribe = async (plan = 'monthly') => {
    try {
      // Use Mollie payment flow
      const response = await axios.post(`${API}/mollie/create-first-payment`, { plan }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
        return { success: true, redirect: true };
      }
      
      await fetchSubscription();
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Er ging iets mis' };
    }
  };

  const cancelSubscription = async () => {
    try {
      await axios.post(`${API}/mollie/cancel-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchSubscription();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Er ging iets mis' };
    }
  };

  const canAccessStory = (story) => {
    if (!story) return false;
    if (story.is_free) return true;
    if (!subscription) return false;
    return subscription.can_access_all_stories;
  };

  const isPremium = user?.is_admin || subscription?.status === 'active' || subscription?.status === 'trial';
  const isTrialing = subscription?.status === 'trial';
  const trialDaysLeft = isTrialing && subscription?.trial_end_date
    ? Math.max(0, Math.ceil((new Date(subscription.trial_end_date) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  return (
    <SubscriptionContext.Provider value={{
      subscription,
      loading,
      isPremium,
      isTrialing,
      trialDaysLeft,
      canAccessStory,
      startTrial,
      subscribe,
      cancelSubscription,
      refreshSubscription: fetchSubscription
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};
